import React, { useState } from 'react';
import { Plus, Trash2, Clock } from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export const AvailabilityForm = ({ availability = [], onAdd, onDelete, loading }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    day_of_week: 'monday',
    start_time: '09:00',
    end_time: '17:00',
  });
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.end_time <= formData.start_time) {
      setError('End time must be after start time');
      return;
    }

    onAdd(formData);
    setFormData({ day_of_week: 'monday', start_time: '09:00', end_time: '17:00' });
    setShowForm(false);
  };

  const groupedAvailability = DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day.value] = availability.filter(a => a.day_of_week === day.value);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="availability-section">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Clock size={20} className="text-primary dark:text-accent-cool" />
          Weekly Availability
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary-light text-white text-sm px-3 py-1.5 rounded-md transition-colors"
          data-testid="add-availability-btn"
        >
          <Plus size={16} />
          Add Slot
        </button>
      </div>

      {showForm && (
        <form 
          onSubmit={handleSubmit} 
          className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 animate-fade-in"
          data-testid="availability-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Day
              </label>
              <select
                value={formData.day_of_week}
                onChange={(e) => setFormData(prev => ({ ...prev, day_of_week: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent"
                data-testid="select-day"
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent"
                data-testid="input-start-time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                End Time
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent"
                data-testid="input-end-time"
              />
            </div>
          </div>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm bg-primary hover:bg-primary-light text-white rounded-md transition-colors disabled:opacity-50"
              data-testid="save-availability-btn"
            >
              {loading ? 'Saving...' : 'Save Slot'}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {DAYS_OF_WEEK.map(day => (
          <div 
            key={day.value}
            className={`p-3 rounded-lg border ${
              groupedAvailability[day.value].length > 0
                ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                : 'bg-slate-50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800'
            }`}
          >
            <p className="font-medium text-sm text-slate-700 dark:text-slate-300 mb-2">{day.label}</p>
            {groupedAvailability[day.value].length > 0 ? (
              <div className="space-y-1.5">
                {groupedAvailability[day.value].map((slot) => (
                  <div 
                    key={slot.id}
                    className="flex items-center justify-between bg-primary/5 dark:bg-accent-cool/10 px-2 py-1.5 rounded text-sm"
                    data-testid={`availability-slot-${slot.id}`}
                  >
                    <span className="text-slate-700 dark:text-slate-300">
                      {slot.start_time} - {slot.end_time}
                    </span>
                    <button
                      onClick={() => onDelete(slot.id)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      data-testid={`delete-slot-${slot.id}`}
                      title="Remove slot"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">No slots</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
